import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import type { User as SupabaseUser, Session } from '@supabase/supabase-js';
import i18n from '@/i18n/config';

export type UserRole = 'admin' | 'shipper' | 'transporter' | 'driver';

export interface User {
  id: string;
  email: string;
  role: UserRole;
  name?: string;
}

interface SignupData {
  email: string;
  password: string;
  companyType: 'shipper' | 'transporter' | 'driver';
  companyName: string;
  foundedDate: string; // YYYY-MM-DD, requis
  firstName?: string;
  lastName?: string;
  phone?: string;
  siret?: string;
  preferredLanguage?: string;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  signup: (data: SignupData) => Promise<void>;
  logout: () => void;
  resetPassword: (email: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        setSession(session);
        
        if (session?.user) {
          // Defer fetching user role and profile
          setTimeout(async () => {
            try {
              const { data: userRole } = await supabase
                .rpc('get_user_role', { _user_id: session.user.id });
              
              const { data: profile } = await supabase
                .from('users')
                .select('first_name, last_name, preferred_language, is_active')
                .eq('id', session.user.id)
                .maybeSingle();

              // Check if user is suspended
              if (profile && !profile.is_active) {
                await supabase.auth.signOut();
                const toastModule = await import('sonner');
                toastModule.toast.error('Votre compte a été suspendu. Contactez l\'administrateur.');
                setUser(null);
                setSession(null);
                setIsLoading(false);
                return;
              }

              // Appliquer la langue préférée
              if (profile?.preferred_language) {
                i18n.changeLanguage(profile.preferred_language);
              }

              setUser({
                id: session.user.id,
                email: session.user.email!,
                role: (userRole as UserRole) || 'shipper',
                name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : undefined,
              });
            } catch (error) {
              console.error('Error fetching user data:', error);
              setUser({
                id: session.user.id,
                email: session.user.email!,
                role: 'shipper',
              });
            }
          }, 0);
        } else {
          setUser(null);
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      
      if (session?.user) {
        setTimeout(async () => {
          try {
            const { data: userRole } = await supabase
              .rpc('get_user_role', { _user_id: session.user.id });
            
            const { data: profile } = await supabase
              .from('users')
              .select('first_name, last_name, preferred_language, is_active')
              .eq('id', session.user.id)
              .maybeSingle();

            // Check if user is suspended
            if (profile && !profile.is_active) {
              await supabase.auth.signOut();
              const toastModule = await import('sonner');
              toastModule.toast.error('Votre compte a été suspendu. Contactez l\'administrateur.');
              setUser(null);
              setSession(null);
              setIsLoading(false);
              return;
            }

            // Appliquer la langue préférée
            if (profile?.preferred_language) {
              i18n.changeLanguage(profile.preferred_language);
            }

            setUser({
              id: session.user.id,
              email: session.user.email!,
              role: (userRole as UserRole) || 'shipper',
              name: profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : undefined,
            });
          } catch (error) {
            console.error('Error fetching user data:', error);
            setUser({
              id: session.user.id,
              email: session.user.email!,
              role: 'shipper',
            });
          }
          setIsLoading(false);
        }, 0);
      } else {
        setIsLoading(false);
      }
    });

    // Listen to realtime changes on users table to detect suspension
    const usersChannel = supabase
      .channel('users-suspension-check')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
        },
        async (payload: any) => {
          // Only act if the updated user is the current user and is now suspended
          if (payload.new && user?.id === payload.new.id && !payload.new.is_active) {
            await supabase.auth.signOut();
            const toastModule = await import('sonner');
            toastModule.toast.error('Votre compte a été suspendu. Contactez l\'administrateur.');
            setUser(null);
            setSession(null);
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
      usersChannel.unsubscribe();
    };
  }, [user?.id]);

  const login = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) throw error;
  };

  const signup = async (data: SignupData) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          company_type: data.companyType,
          company_name: data.companyName,
          founded_date: data.foundedDate,
          first_name: data.firstName || '',
          last_name: data.lastName || '',
          phone: data.phone || '',
          siret: data.siret || '',
          preferred_language: data.preferredLanguage || 'fr',
        },
      },
    });
    
    if (error) throw error;

    // E-mail de bienvenue via Resend (expéditeur info@myvelox.eu)
    try {
      await supabase.functions.invoke('send-signup-welcome', { body: { email: data.email } });
    } catch (e) {
      console.error('send-signup-welcome failed:', e);
    }
  };

  const logout = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
    setUser(null);
    setSession(null);
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/reset-password`;

    // Envoi via notre propre service (Resend, expéditeur info@myvelox.eu)
    const { error } = await supabase.functions.invoke('send-password-reset', {
      body: { email, redirect_to: redirectUrl },
    });

    if (error) throw error;
  };


  return (
    <AuthContext.Provider value={{ user, session, isLoading, login, signup, logout, resetPassword }}>
      {children}
    </AuthContext.Provider>
  );
};
